import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Card, Badge, Form, InputGroup } from "react-bootstrap";
import { Eye, Plus, Trash2, Edit, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getAllBlogs, deleteBlog } from "../../api/blog";
import { toast } from "react-toastify";
import moment from "moment";
import Const from "../../utils/constant";
import BlogButton from "../../components/Buttons";
import RenderImage from "../../components/image-render";
import Modal from "../../components/modal";
import { debounce } from "lodash";

const BlogsList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const navigate = useNavigate();

  const fetchBlogs = async (search = "") => {
    try {
      setLoading(true);
      const data = await getAllBlogs({ search });
      setBlogs(data);
    } catch (error) {
      console.error("Error fetching blogs:", error);
      toast.error("Failed to load blogs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const debouncedSearch = useCallback(
    debounce((nextValue) => fetchBlogs(nextValue), 500),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const openDeleteModal = (blog) => {
    setSelectedBlog(blog);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedBlog) {
      try {
        await deleteBlog(selectedBlog._id);
        toast.success("Blog deleted successfully");
        fetchBlogs(searchTerm);
      } catch (error) {
        toast.error("Failed to delete blog");
      }
    }
  };

  return (
    <div className="container-fluid py-4 min-vh-100 bg-light/30">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1 text-[#1a1a4e]">Blogs</h2>
          <p className="text-muted small mb-0">Manage your website articles and insights</p>
        </div>
        <div className="d-flex gap-3 align-items-center">
          <InputGroup className="bg-white rounded-xl overflow-hidden border shadow-sm" style={{ maxWidth: "300px" }}>
            <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
              <Search size={18} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by title or slug..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="border-0 shadow-none ps-2 py-2"
            />
          </InputGroup>
          <BlogButton variant="outline" onClick={() => navigate("/blogs/add")} className="flex-shrink-0">
            <Plus size={18} />
            Add New Blog
          </BlogButton>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-3xl overflow-hidden">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="align-middle mb-0 custom-admin-table">
              <thead className="bg-[#1a1a4e] text-white">
                <tr>
                  <th className="px-4 py-3 border-0">Banner</th>
                  <th className="py-3 border-0">Title</th>
                  <th className="py-3 border-0">Slug</th>
                  <th className="py-3 border-0 text-center">Date</th>
                  <th className="px-4 py-3 border-0 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </td>
                  </tr>
                ) : blogs.length > 0 ? (
                  blogs.map((blog) => (
                    <tr key={blog._id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="rounded-xl overflow-hidden shadow-sm" style={{ width: "80px", height: "50px" }}>
                          <RenderImage
                            keyField={blog.banner}
                            alt={blog.title}
                            className="w-100 h-100 object-cover"
                          />
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="fw-semibold text-[#1a1a4e]">{blog.title}</div>
                        <div className="text-muted small text-truncate" style={{ maxWidth: "250px" }}>
                          {blog.description}
                        </div>
                      </td>
                      <td className="py-3">
                        <Badge bg="light" className="text-muted border px-2 py-1 fw-normal">
                          {blog.slug}
                        </Badge>
                      </td>
                      <td className="py-3 text-center text-muted small">
                        {moment(blog.createdAt).format(Const.DATE_TIME_FORMATS.DATE_UI)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            className="p-2 rounded-full text-blue-500 transition-all duration-200 hover:bg-blue-500 hover:text-white"
                            onClick={() => navigate(`/blogs/edit/${blog._id}`)}
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="p-2 rounded-full text-cyan-500 transition-all duration-200 hover:bg-cyan-500 hover:text-white"
                            onClick={() => navigate(`/blogs/edit/${blog._id}`)}
                            title="View"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="p-2 rounded-full text-red-500 transition-all duration-200 hover:bg-red-500 hover:text-white"
                            onClick={() => openDeleteModal(blog)}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>

                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-5 text-muted">
                      {searchTerm ? `No blogs found for "${searchTerm}"` : "No blogs found. Start by adding one!"}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Modal
        title="Delete Blog"
        desc={`Are you sure you want to delete "${selectedBlog?.title}"? This action cannot be undone.`}
        isModalOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        handleSubmit={confirmDelete}
        confirmButtonLabel="Delete Blog"
        cancelButtonLabel="Keep Blog"
        variant="danger"
        size="sm"
      />
    </div>
  );
};

export default BlogsList;
